import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LuGlobe, LuLock, LuMail, LuUsers } from 'react-icons/lu';
import { NOTEBOOK_ACCESS } from '../../utils/notebookSharing';
import PlanTierLabel from '../../components/billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import { usePlanGate } from '../../hooks/usePlanGate';

const OPTIONS = [
  {
    id: NOTEBOOK_ACCESS.PUBLIC,
    icon: LuGlobe,
    title: 'Public',
    hint: 'Anyone can discover and read this notebook in Learn.',
    accent: 'public',
  },
  {
    id: NOTEBOOK_ACCESS.PRIVATE,
    icon: LuLock,
    title: 'Private',
    hint: 'Only you can open it — hidden from public lists.',
    planTier: 'Pro',
    accent: 'private',
  },
  {
    id: NOTEBOOK_ACCESS.TEAM,
    icon: LuUsers,
    title: 'Team',
    hint: 'Share with everyone in your organization workspace.',
    planTier: 'Pro',
    accent: 'team',
  },
  {
    id: NOTEBOOK_ACCESS.EMAIL,
    icon: LuMail,
    title: 'Email invite',
    hint: 'Invite one collaborator by email.',
    planTier: 'Pro',
    accent: 'email',
  },
];

export default function LearnNotebookSharingPanel({
  accessMode,
  onAccessModeChange,
  memberEmail,
  onMemberEmailChange,
  emailError = '',
  compact = true,
  emailInputId = 'nb-invite-email',
}) {
  const { blockUnlessFeature } = usePlanGate();

  const selected = useMemo(
    () => OPTIONS.find((o) => o.id === accessMode) || OPTIONS[0],
    [accessMode],
  );

  const select = (opt) => {
    if (opt.id === NOTEBOOK_ACCESS.PRIVATE && blockUnlessFeature(FEATURE.NOTEBOOK_PRIVATE)) {
      return;
    }
    if (opt.id === NOTEBOOK_ACCESS.TEAM && blockUnlessFeature(FEATURE.NOTEBOOK_SHARE_TEAM)) {
      return;
    }
    if (opt.id === NOTEBOOK_ACCESS.EMAIL && blockUnlessFeature(FEATURE.NOTEBOOK_INVITE_EMAIL)) {
      return;
    }
    onAccessModeChange(opt.id);
  };

  if (!compact) {
    return (
      <div className="learn-sharing-step">
        <header className="learn-sharing-step-head">
          <h2 className="learn-sharing-step-title">Who can access this notebook?</h2>
          <p className="learn-sharing-step-lead">
            Public is on Free. Options marked Pro or Team need the matching plan at save time.
          </p>
        </header>

        <div className="learn-access-cards" role="radiogroup" aria-label="Notebook access">
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = accessMode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`learn-access-card learn-access-card--${opt.accent}${isSelected ? ' is-selected' : ''}`}
                onClick={() => select(opt)}
              >
                <span className="learn-access-card-badges">
                  {opt.planTier && <PlanTierLabel tier={opt.planTier} />}
                </span>
                <span className="learn-access-card-main">
                  <span className={`learn-access-card-icon learn-access-card-icon--${opt.accent}`} aria-hidden>
                    <Icon />
                  </span>
                  <span className="learn-access-card-copy">
                    <span className="learn-access-card-title">{opt.title}</span>
                    <span className="learn-access-card-hint">{opt.hint}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="learn-sharing-step-foot">
          <p className="learn-access-selection-text" role="status">
            <strong>{selected.title}:</strong> {selected.hint}
            {selected.planTier && (
              <>
                {' '}
                <PlanTierLabel tier={selected.planTier} />
              </>
            )}
          </p>
        </div>

        {accessMode === NOTEBOOK_ACCESS.EMAIL && (
          <div className="learn-form-field learn-access-email learn-access-email--step">
            <label htmlFor={emailInputId}>
              Collaborator email <PlanTierLabel tier="Pro" />
            </label>
            <input
              id={emailInputId}
              type="email"
              className="learn-input"
              value={memberEmail}
              onChange={(e) => onMemberEmailChange(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
            />
            {emailError && <p className="learn-field-hint learn-field-hint--error">{emailError}</p>}
            {!emailError && (
              <p className="learn-field-hint">They must already have a QnaRead account with this email.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="learn-access-panel learn-access-panel--row learn-access-panel--compact learn-access-panel--stacked">
      <div className="learn-access-options learn-access-options--segmented" role="radiogroup" aria-label="Notebook access">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isSelected = accessMode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${opt.title}. ${opt.hint}`}
              title={opt.hint}
              className={`learn-access-option${isSelected ? ' is-selected' : ''}`}
              onClick={() => select(opt)}
            >
              <Icon className="learn-access-option-svg" aria-hidden />
              <span className="learn-access-option-title">{opt.title}</span>
              {opt.planTier && <PlanTierLabel tier={opt.planTier} />}
            </button>
          );
        })}
      </div>

      {accessMode === NOTEBOOK_ACCESS.EMAIL && (
        <div className="learn-access-email-inline">
          <input
            id={emailInputId}
            type="email"
            className="learn-input learn-input--compact"
            value={memberEmail}
            onChange={(e) => onMemberEmailChange(e.target.value)}
            placeholder="Collaborator email"
            autoComplete="email"
            aria-label="Collaborator email"
          />
          {emailError && <span className="learn-access-email-error">{emailError}</span>}
        </div>
      )}

      <p className="learn-access-upgrade learn-access-upgrade--stacked">
        <Link to="/settings?tab=billing">Compare plans</Link>
        <span>Private needs Pro or Team; team-wide and email invite need the right plan at save.</span>
      </p>
    </div>
  );
}
